import {
  format,
  subWeeks,
  subMonths,
  startOfWeek,
  startOfMonth,
  endOfWeek,
  addWeeks,
  addMonths,
  endOfMonth,
} from "date-fns";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { theme } from "@answersnow/pkg-theme";

/** @typedef {import("api/typesDef").DeliveredHours} DeliveredHours */

// @ts-expect-error - pkg-theme typings will be addressed in a future type iteration
const { purple, grey } = theme.palette;
/**
 * The colors for the clients in the bar chart
 * @type {string[]}
 */
export const colors = [
  purple[800],
  purple[700],
  purple[600],
  purple[500],
  purple[400],
  purple[300],
  grey[300],
  grey[400],
  grey[500],
  grey[600],
  grey[700],
];

/**
 * The filters for the period
 * @type {{BY_WEEKS: "weeks", BY_MONTHS: "months"}}
 */
export const FILTERS = {
  BY_WEEKS: "weeks",
  BY_MONTHS: "months",
};

/**
 * Get the startDate and endDate for GetClinicianHours API:
 * - For weeks, starts from the last 8 weeks not including the current week
 * - For months, starts from the first day of the past 12 months not including the current month
 * @param {"weeks" | "months"} timeUnit - The time unit to filter the data by (weeks or months)
 * @returns {{startDate: Date, endDate: Date}} The start and end date
 */
export const getStartDateAndEndDate = (timeUnit = FILTERS.BY_WEEKS) => {
  const today = new Date();
  const startDate =
    timeUnit === FILTERS.BY_WEEKS
      ? startOfWeek(subWeeks(today, 7), { weekStartsOn: 1 })
      : startOfMonth(subMonths(today, 11));
  const endDate = today;

  return { startDate, endDate };
};

/**
 * Get the startDate and endDate for AuthSchedDelivered component:
 * - 6 back and 6 forward (Total 12)
 * @param {"weeks" | "months"} timeUnit - The time unit to filter the data by (weeks or months)
 * @returns {{startDate: Date, endDate: Date}} The start and end date
 */
export const getAuthSchedDeliveredDates = (timeUnit = FILTERS.BY_WEEKS) => {
  const today = new Date();
  if (timeUnit === FILTERS.BY_WEEKS) {
    // 6 back and 6 forward = 12 total
    const startDate = startOfWeek(subWeeks(today, 6), { weekStartsOn: 1 });
    const endDate = endOfWeek(addWeeks(today, 5), { weekStartsOn: 1 });
    return { startDate, endDate };
  } else {
    // 6 back and 6 forward = 12 total
    const startDate = startOfMonth(subMonths(today, 6));
    const endDate = endOfMonth(addMonths(today, 5));
    return { startDate, endDate };
  }
};

/**
 * Get the startDate and endDate for CancellationsNoShows component:
 * - 12 weeks looking backwards
 * - Stick to default 12 months back for months view if not specified
 * @param {"weeks" | "months"} timeUnit - The time unit to filter the data by (weeks or months)
 * @returns {{startDate: Date, endDate: Date}} The start and end date
 */
export const getCancellationsNoShowsDates = (timeUnit = FILTERS.BY_WEEKS) => {
  const today = new Date();
  if (timeUnit === FILTERS.BY_WEEKS) {
    // 12 weeks looking backwards
    const startDate = startOfWeek(subWeeks(today, 11), { weekStartsOn: 1 });
    const endDate = today;
    return { startDate, endDate };
  } else {
    // Stick to default 12 months back for months view if not specified
    return getStartDateAndEndDate(timeUnit);
  }
};

/**
 * Specific colors for the AuthSchedDelivered chart
 * @type {{authorized: string, scheduled: string, delivered: string}}
 */
export const authSchedDeliveredColors = {
  authorized: purple[400],
  scheduled: purple[500],
  delivered: purple[600],
};

/**
 * Generate the time labels for the X-axis of the chart (8 past weeks or 12 past months)
 * @param {{date: string, total_duration: number}[]} dataArray - The data array to generate the labels from
 * @param {string} timeUnit - The time unit to filter the data by (weeks or months)
 * @param {boolean} isSmallScreen - Whether the screen is small
 * @returns {string[]} The dynamic labels for the X-axis of the chart
 */
export const generateDynamicLabels = (dataArray, timeUnit, isSmallScreen = false) => {
  const today = new Date();
  const currentYear = format(today, "yyyy");

  return (
    dataArray?.map((item) => {
      const date = dateWithoutTimezone(item.date);

      if (isSmallScreen) {
        return format(date, timeUnit === FILTERS.BY_WEEKS ? "M/d" : "M/yy");
      }

      if (timeUnit === FILTERS.BY_WEEKS) {
        return format(date, "MMM dd");
      } else {
        // For months, add year if it's different from current year
        const labelMonth = format(date, "MMM");
        const labelYear = format(date, "yyyy");
        return `${labelMonth}${labelYear !== currentYear ? ` ${labelYear}` : ""}`;
      }
    }) || []
  );
};

/**
 * Generate dynamic date range for chart tooltips
 * @param {Array} dataArray
 * @param {number} dataIndex
 * @param {string} period
 * @returns {string}
 */
export const getTooltipDateRange = (dataArray, dataIndex, period) => {
  if (!dataArray || dataIndex === undefined || !dataArray[dataIndex]) {
    return "No date range available";
  }

  const currentDataPoint = dataArray[dataIndex];
  if (!currentDataPoint || !currentDataPoint.date) {
    return "No date available";
  }

  const currentDate = dateWithoutTimezone(currentDataPoint.date);

  if (period === "1 week") {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    return `${format(weekStart, "MMM do")} thru ${format(weekEnd, "MMM do")}`;
  } else if (period === "1 month") {
    return format(currentDate, "MMMM");
  }

  return format(currentDate, "MMM do, yyyy");
};

// Authorized hours / delivered hours -> percentage for the last week
export const getLastWeekDeliveredPorcent = (hours) => {
  if (!hours || !hours.authorized || !hours.delivered) return 0;

  const { authorized, delivered } = hours;
  const today = new Date();
  const startOfThisWeek = startOfWeek(today, { weekStartsOn: 1 });

  // Find the index of the latest entry whose date is before the start of this week.
  let targetIndex = -1;
  for (let i = authorized.length - 1; i >= 0; i--) {
    const itemDate = dateWithoutTimezone(authorized[i].date);
    if (itemDate < startOfThisWeek) {
      targetIndex = i;
      break;
    }
  }

  if (targetIndex === -1) return 0;

  const authorizedHours = authorized[targetIndex]?.total_duration || 0;
  const deliveredHours = delivered[targetIndex]?.total_duration || 0;

  return authorizedHours > 0
    ? Math.round((parseFloat(deliveredHours) / parseFloat(authorizedHours)) * 100)
    : 0;
};

export const isHoursEmpty = (hours, isHoursLoading) => {
  return (
    !isHoursLoading && (!hours || hours?.authorized?.length === 0 || hours?.delivered?.length === 0)
  );
};

/**
 * `getDataArraysByCode` function to get the data arrays by code for the delivered hours bar chart.
 * @param {DeliveredHours[]} dataHours - The delivered hours
 * @returns {Array<{
 *   [key: string]: {
 *     code: string,
 *     dates: string[],
 *     hours: number[],
 *   }
 * }>}
 */
export function getDataArraysByCode(dataHours) {
  if (!dataHours || dataHours?.length === 0) {
    return [];
  }

  const dataArraysByCode = {};
  // create an array of 0s to store the duration by code for each period
  const noHoursArray = Array(dataHours.length).fill(0);
  const periodDates = [];
  const totalDuration = [];

  dataHours.forEach((hour, index) => {
    // store the date in the periodDates array for using in the tooltip of the chart
    periodDates.push(hour.date);
    // store the total_duration in the totalDuration array for using in the subtitle of the tooltip of the chart
    totalDuration.push(hour.total_duration);

    // if the total_duration is 0, skip the iteration
    if (hour.total_duration === 0) {
      return;
    }

    // iterate over the duration_by_code and store the duration and the date in the dataArraysByCode
    Object.entries(hour.duration_by_code).forEach(([code, duration]) => {
      if (!dataArraysByCode[code]) {
        dataArraysByCode[code] = {
          code,
          hours: [...noHoursArray],
        };
      }
      // replace the 0 with the duration for the current index
      dataArraysByCode[code].hours[index] = duration;
    });
  });

  // return the dataArraysByCode as an array of objects with the code, date and hours
  return Object.values(dataArraysByCode).map((data) => ({
    code: data.code,
    hours: data.hours,
    dates: periodDates,
    totalDuration,
  }));
}

/**
 * Formats the custom date range for display, on Date Range Picker.
 * @param {[Date | null, Date | null]} customDateRange - The custom date range to format
 * @returns {string} The formatted date range string
 */
export const formatDateRange = (customDateRange: [Date | null, Date | null]): string => {
  const [start, end] = customDateRange;
  if (start && end) return `${format(start, "MM/dd/yyyy")} - ${format(end, "MM/dd/yyyy")}`;
  if (start) return format(start, "MM/dd/yyyy");
  return "";
};
